import React from 'react';
import { useTranslation } from "react-i18next";
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import SystemNameplate from '../SystemNameplate';
import { SideNavigation } from '@dreebit-registry/vsm-microservice-components';
import { Box } from '@mui/material';
import { SystemVisualizationUrl } from '../../graphql/queries/systemVisualizationUrl';
import { getVisualizationUrl } from '../../utils/visualizationUrl';

interface Props {
    systemId: string;
    systemType: string | null;
    hasIotConnection: boolean;
    isContractValid: boolean;
    isContractExpired: boolean;
    isIoTAdmin: boolean;
}

const SystemDetailNavigation = ({ systemId, systemType, hasIotConnection, isContractValid, isContractExpired, isIoTAdmin }: Props) => {
    const { t } = useTranslation();

    // IoT admins may view existing data for an expired or missing contract.
    // Keep in sync with canViewIoT (src/utils/isContractValid.ts).
    const canMonitorIoT = isContractValid || isIoTAdmin;
    const showIotNav = !!(systemType && canMonitorIoT);
    // Non-admins keep seeing the IoT entries after expiry, but locked.
    const isLockedIoT = !!systemType && isContractExpired && !isIoTAdmin;

    const visualizationQuery = useQuery(SystemVisualizationUrl, { variables: { id: systemId } });
    const visualizationUrl = getVisualizationUrl(_.get(visualizationQuery, 'data.system.attributeList.attributes'));

    const navItems = [
        { label: t('Dashboard'), to: `/systems/${systemId}/dashboard` },
        ...(showIotNav ? [
            { label: t('Maintenance'), to: `/systems/${systemId}/maintenance` },
            { label: t('Service'), to: `/systems/${systemId}/service` },
            { label: t('Alerts'), to: `/systems/${systemId}/alerts` },
        ] : []),
        ...(isLockedIoT ? [
            { label: t('Maintenance'), to: `/systems/${systemId}/maintenance`, locked: true, lockedTooltip: t('The IoT contract for this system has expired.') },
            { label: t('Service'), to: `/systems/${systemId}/service`, locked: true, lockedTooltip: t('The IoT contract for this system has expired.') },
            { label: t('Alerts'), to: `/systems/${systemId}/alerts`, locked: true, lockedTooltip: t('The IoT contract for this system has expired.') },
        ] : []),
        { label: t('Documents'), to: `/systems/${systemId}/files` },
        ...(visualizationUrl ? [
            { label: t('Visualization'), to: visualizationUrl, isExternalLink: true },
        ] : []),
    ];

    return (
        <div className="flex-col">
            <SystemNameplate systemId={systemId} />

            <Box sx={{ pt: "8px" }}>
                <SideNavigation navItems={navItems} />
            </Box>
        </div>
    );
};

export default SystemDetailNavigation;
